import React, { useMemo } from "react";
import { MDXProvider } from "@mdx-js/react";
import { renderNode } from "~/views/components/content/markdown";
import rehypeRaw from "rehype-raw";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";

type Props = {
  node?: any;
};

const components = {
  h2: ({ children, ...props }: any) => (
    <h2
      className="text-xl sm:text-2xl font-bold text-neutral-30 mt-8 mb-4 leading-tight"
      {...props}>
      {children}
    </h2>
  ),

  h3: ({ children, ...props }: any) => (
    <h3
      className="text-lg sm:text-xl font-bold text-neutral-30 mt-6 mb-3 leading-tight"
      {...props}>
      {children}
    </h3>
  ),

  h4: ({ children, ...props }: any) => (
    <h4
      className="text-base sm:text-lg font-bold text-neutral-30 mt-4 mb-2 leading-tight"
      {...props}>
      {children}
    </h4>
  ),

  h5: ({ children, ...props }: any) => (
    <h5
      className="text-sm sm:text-base font-bold text-neutral-30 mt-3 mb-2 leading-tight"
      {...props}>
      {children}
    </h5>
  ),

  // Paragraphs
  p: ({ children, ...props }: any) => (
    <p className="text-neutral-30 leading-8 font-regular my-4" {...props}>
      {children}
    </p>
  ),

  // Lists
  ul: ({ children, ...props }: any) => (
    <ul className="marker:text-cyan list-outside list-disc" {...props}>
      {children}
    </ul>
  ),

  ol: ({ children, ...props }: any) => (
    <ol className="list-decimal list-outside marker:text-cyan" {...props}>
      {children}
    </ol>
  ),

  li: ({ children, ...props }: any) => (
    <li
      className="richtext-list-item ml-4 py-3 sm:py-4 font-medium text-sm sm:text-base leading-[30px] sm:leading-[32px] text-neutral-30"
      {...props}>
      {children}
    </li>
  ),

  // Blockquotes
  blockquote: ({ children, ...props }: any) => (
    <blockquote
      className="border-l-4 border-cyan pl-4 my-4 italic text-neutral-30 leading-8"
      {...props}>
      {children}
    </blockquote>
  ),

  // Tables
  table: ({ children, ...props }: any) => (
    <div className="overflow-x-auto my-4">
      <table
        className="min-w-full border-collapse border border-neutral-20"
        {...props}>
        {children}
      </table>
    </div>
  ),

  tableRow: ({ children, ...props }: any) => (
    <tr className="border-b border-neutral-20" {...props}>
      {children}
    </tr>
  ),

  tableCell: ({ children, ...props }: any) => (
    <td
      className="border border-neutral-20 px-4 py-2 text-neutral-30 text-sm sm:text-base"
      {...props}>
      {children}
    </td>
  ),

  th: ({ children, ...props }: any) => (
    <th
      className="border border-neutral-20 px-4 py-2 bg-neutral-10 font-bold text-neutral-30 text-sm sm:text-base text-left"
      {...props}>
      {children}
    </th>
  ),

  // Text formatting
  strong: ({ children, ...props }: any) => (
    <strong className="font-bold" {...props}>
      {children}
    </strong>
  ),

  em: ({ children, ...props }: any) => (
    <em className="italic font-medium" {...props}>
      {children}
    </em>
  ),

  // Links
  a: ({ children, href, ...props }: any) => (
    <a
      href={href}
      className="text-cyan hover:text-cyan-dark underline font-medium transition-colors duration-200"
      {...props}>
      {children}
    </a>
  ),
};

function Note({ node }: Props) {
  const { frontmatter, body } = node;

  const file = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .parse(body);

  const renderedContent = useMemo(() => {
    return renderNode(file, 0, components);
  }, [file]);

  return (
    <div className="my-8 sm:my-16 component-note">
      <span className="relative inline-block leading-[20px] text-xs font-bold text-neutral-60 py-[2px] px-2 bg-neutral-95 rounded align-middle mr-2 mb-1">
        {frontmatter.label}
      </span>
      <span className="text-neutral-30 text-2xs leading-6 sm:text-xs sm:leading-7">
        <MDXProvider components={components}>{renderedContent}</MDXProvider>
      </span>
    </div>
  );
}

export default Note;
